// @ts-check

/**
 * Returns the value of the environment variable.
 * If the variable is not defined, throws an error.
 * If the variable is empty, throws an error.
 * If the variable is not a string, throws an error.
 * If the variable is not provided, throws an error.
 * @param {string} variable
 * @returns {string}
 * @throws {Error}
 * @example
 * const variable = getVariableOrThrow('VARIABLE_NAME');
 * console.log(variable);
 * // => 'value'
 * @example
 * const variable = getVariableOrThrow();
 * // => throw new Error('Please, provide a name for a variable.');
 */
export const getVariableOrThrow = (variable) => {
  if (!variable) throw new Error('Please, provide a name for a variable.');
  const value = process.env[variable];
  if (!value)
    throw new Error(`There is no variable with name: ${variable} in the current environment`);
  return value;
};

export const getVariableOrDefault = (variable, defaultValue) =>
  getVariableOrNull(variable) || defaultValue;

/**
 * Returns the value of the environment variable or null.
 * If the variable is not defined, returns null.
 * @param {string} variable
 * @returns {string | null}
 */
export const getVariableOrNull = (variable) => {
  const value = process.env[variable];
  if (!value) return null;
  return value;
};

/**
 * Returns the current env based on the url.
 * If url contains 'dev', returns 'development'.
 * If url contains 'stg', returns 'staging'.
 * If nothing matches, returns 'production'.
 * @param {string} url
 */
export const getEnvFromUrl = (url) => {
  if (url.includes('localhost')) return 'development';
  if (url.includes('127.0.0.1')) return 'development';
  if (url.includes('dev')) return 'development';
  if (url.includes('stg')) return 'staging';
  if (url.includes('preprod')) return 'preprod';
  if (url.includes('storybook')) return 'staging';
  if (url.includes('cloudfront')) return 'staging';
  return 'production';
};

/**
 * Returns the value of the environment variable NODE_ENV.
 * If the variable is not defined, throws an error.
 *
 * @param {{
 *   shortened: boolean | undefined
 * }} params
 * @returns {import("./envs").Env | string}
 */
export const useEnv = ({ shortened } = { shortened: false }) => {
  const _env = getEnvFromUrl(window.location.hostname);
  const envs = {
    development: 'dev',
    staging: 'stg',
    production: 'prod',
    preprod: 'preprod',
  };
  return shortened ? envs[_env] : _env;
};
